import { z } from 'zod'

/**
 * Wire schemas for managerium's `/v1/dashboards` endpoints (see managerium's
 * docs/adr/v02/openapi/openapi.yaml). Only one dashboard (set per environment) is used so far.
 */

/** The granularities a section's cards can be drawn in. */
export const timeframes = ['YTD', 'Monthly', 'Quarterly', 'Annual'] as const
const timeframeSchema = z.enum(timeframes)
export type Timeframe = z.infer<typeof timeframeSchema>
export const DEFAULT_TIMEFRAME: Timeframe = 'YTD'

/** What a card's change compares against, by timeframe. */
const comparisonSchema = z.enum(['MoM', 'QoQ', 'YoY'])
export type Comparison = z.infer<typeof comparisonSchema>

/** The dashboard filters a card takes; the UI only sends a filter to cards listing it. */
export const MARKET_FILTER = 'market'
export const TIMEFRAME_FILTER = 'timeframe'

/** The dashboard filters a card can list besides market and timeframe (regulatory deep dive). */
export const C_LEVEL_FILTER = 'c_level'
export const CRITICALITY_FILTER = 'criticality'
export const TAG_FILTER = 'tag'

/** Where a deep-dive card sits in its subsection's grid. */
const cardLayoutSchema = z.object({
  size: z.enum(['single-metric', 'wide', 'half-wide', 'half']).nullish(),
  /** How many of the subsection's `divisions` the card spans. */
  blocks: z.number().nullish(),
})

/** How a deep-dive chart card is drawn. */
const cardChartSchema = z.object({
  /** Overrides the Metabase display (area, bar, map...). */
  chartType: z.string().nullish(),
  stacked: z.boolean().optional(),
  stackedTotals: z.boolean().optional(),
  /** With `stacked`: columns of each category's shares (100%) rather than its sums. */
  percentStacked: z.boolean().optional(),
  dateFormat: z.enum(['month']).nullish(),
  xLabels: z.boolean().optional(),
})

export type CardChart = z.infer<typeof cardChartSchema>

const cardStubSchema = z.object({
  cardId: z.number(),
  name: z.string(),
  display: z.string(),
  /** What the metric means, for the card's (i) tooltip. */
  description: z.string().nullish(),
  /** The deep-dive section this card links to, when the backend picked one. */
  deepDiveId: z.string().nullish(),
  /** The filters ('market', 'timeframe') that apply to the card; none when absent. */
  filters: z.array(z.string()).nullish(),
  /** Deep-dive cards only: where the card sits in its subsection's grid. */
  layout: cardLayoutSchema.nullish(),
  /** Deep-dive cards only: how the card is drawn. */
  chart: cardChartSchema.nullish(),
  /**
   * A tile with no data yet: not requested, drawn as 'Data pending' (synthetic
   * negative `cardId`).
   */
  pending: z.literal(true).optional(),
})

export type CardStub = z.infer<typeof cardStubSchema>

const sectionKindSchema = z.enum(['section', 'deep_dive'])

export type DashboardSection = {
  id: string
  title: string
  kind: z.infer<typeof sectionKindSchema>
  /** A deep dive's cards are always the flat list of every subsection's. */
  cards: CardStub[]
  /** Deep dives with headings: one group per subsection, `title` being its heading (maybe blank). */
  subsections?: DashboardSection[] | undefined
  /** Subsections: equal grid tracks that the cards' `layout.blocks` span. */
  divisions?: number | null | undefined
  /** Home sections: the deep dives its cards lead to, in order. */
  deepDiveIds?: string[] | null | undefined
}

/**
 * Bump when a Managerium response changes shape. Cached responses are kept
 * across reloads (see shared/api/queryClient.ts) and are discarded when this
 * changes, so nobody is served the old shape.
 */
export const CONTRACT_VERSION = '11'

const dashboardSectionSchema: z.ZodType<DashboardSection> = z.lazy(() =>
  z.object({
    id: z.string(),
    title: z.string(),
    kind: sectionKindSchema,
    cards: z.array(cardStubSchema),
    subsections: z.array(dashboardSectionSchema).optional(),
    divisions: z.number().nullish(),
    deepDiveIds: z.array(z.string()).nullish(),
  })
)

const periodOptionSchema = z.object({ value: z.number(), label: z.string() })

const marketSchema = z.object({
  id: z.string(),
  label: z.string(),
  /** A country code for the flag (`de`), when the market has one. */
  flag: z.string().nullish(),
})

const periodsSchema = z.object({
  default: z.number(),
  options: z.array(periodOptionSchema),
})

/** One timeframe on offer: what its change compares against, and its ranges. */
const timeframeOptionSchema = z.object({
  id: timeframeSchema,
  comparison: comparisonSchema,
  periods: periodsSchema,
})

export type TimeframeOption = z.infer<typeof timeframeOptionSchema>

/** The dashboard-wide filters: the range of months, the markets and the timeframes. */
const dashboardFiltersSchema = z.object({
  periods: periodsSchema,
  markets: z.array(marketSchema),
  /** Present when some card takes the timeframe filter. */
  timeframes: z.array(timeframeOptionSchema).optional(),
})

export type DashboardFilters = z.infer<typeof dashboardFiltersSchema>

/** A named default timeframe for the sections, e.g. "FY outlook (2026)" -> YTD. */
const viewModeSchema = z.object({
  id: z.string(),
  label: z.string(),
  timeframe: timeframeSchema,
})

export type ViewMode = z.infer<typeof viewModeSchema>

export const dashboardStructureSchema = z.object({
  dashboardId: z.number(),
  instance: z.string(),
  name: z.string(),
  lastUpdated: z.string().nullish(),
  sections: z.array(dashboardSectionSchema),
  filters: dashboardFiltersSchema.optional(),
  viewModes: z.array(viewModeSchema).optional(),
})

export type DashboardStructure = z.infer<typeof dashboardStructureSchema>

const trendDirectionSchema = z.enum(['up', 'down', 'flat'])
export type TrendDirection = z.infer<typeof trendDirectionSchema>

/** How a card's numbers are shown; percent cards hold raw fractions (0.12 = 12%). */
const cardFormatSchema = z.object({
  style: z.enum(['number', 'currency', 'percent']),
  decimals: z.number().nullable(),
})

export type CardFormat = z.infer<typeof cardFormatSchema>

const defaultFormat: CardFormat = { style: 'number', decimals: null }

/**
 * How a card's change is expressed (relative %, or the plain difference in
 * percentage points / points), coloured, and how many decimals its headline keeps.
 * Absent on older payloads, hence optional: see `resolveDisplay`.
 */
const cardDisplaySchema = z.object({
  changeUnit: z.enum(['relative', 'ppt', 'pts']).default('relative'),
  lowerIsBetter: z.boolean().default(false),
  valueDecimals: z.number().nullish(),
})

export type CardDisplay = z.infer<typeof cardDisplaySchema>

const defaultDisplay: CardDisplay = {
  changeUnit: 'relative',
  lowerIsBetter: false,
}

export const resolveDisplay = (
  card: { display?: CardDisplay | undefined } | undefined
): CardDisplay => card?.display ?? defaultDisplay

const kpiCardSchema = z.object({
  kind: z.literal('kpi'),
  cardId: z.number(),
  name: z.string(),
  unit: z.string().nullish(),
  format: cardFormatSchema.default(defaultFormat),
  value: z.number(),
  previousValue: z.number().nullish(),
  trendDirection: trendDirectionSchema,
  display: cardDisplaySchema.optional(),
})

/** The chart displays Managerium's Metabase mapper accepts. */
export const chartTypes = [
  'area',
  'line',
  'bar',
  'waterfall',
  'pie',
  'scatter',
  'combo',
  'table',
  // Deep-dive overrides: a map of European markets and a horizontal bar; circle is drawn as an area.
  'map',
  'hbar',
  'circle',
  // Managerium also names these; no card is drawn as them yet.
  'funnel',
  'kpi',
] as const

const chartSeriesSchema = z.object({
  name: z.string(),
  data: z.array(z.number().nullable()),
  /** A benchmark (target, budget, forecast, outlook): never part of the totals. */
  reference: z.boolean().nullish(),
  /** The y axis the series is read on; the primary (left) one when absent. */
  axis: z.enum(['primary', 'secondary']).nullish(),
  /** How the series is drawn in a dual-axis chart; the chart's own way when absent. */
  render: z.enum(['column', 'line']).nullish(),
})

export type ChartSeries = z.infer<typeof chartSeriesSchema>

const reportedSchema = z.object({
  value: z.number().nullish(),
  previousValue: z.number().nullish(),
  /** What the two compare, e.g. 'YTD YoY'. */
  comparison: z.string().nullish(),
})

/** What a table column holds: values, the change between two, or a share of the total. */
const tableColumnSchema = z.strictObject({
  id: z.string(),
  label: z.string(),
  kind: z.enum(['value', 'delta', 'deltaPercent', 'share', 'text']),
  format: cardFormatSchema.partial({ decimals: true }).optional(),
  /** A fall is good news: a delta column's green and red swap. */
  lowerIsBetter: z.boolean().optional(),
  /**
   * How a value column's figure is drawn: `bar` a bar sized against the column's
   * largest, `diverge` a bar either side of the column's average.
   */
  display: z.enum(['bar', 'diverge']).optional(),
})

const tableRowSchema = z.strictObject({
  id: z.string(),
  label: z.string(),
  /** 1 is an indented child row. */
  level: z.union([z.literal(0), z.literal(1)]),
  /** group: the shaded sum of its children; total: the bold last row. */
  kind: z.enum(['row', 'group', 'total']),
  /** One per column, in its order; null is not available. */
  cells: z.array(z.union([z.number(), z.string(), z.null()])),
})

/**
 * A deep-dive table card, built server-side. The numbers (deltas, shares,
 * sums) all come from the backend: the UI only formats and colours them.
 */
const tableSchema = z.strictObject({
  columns: z.array(tableColumnSchema),
  rows: z.array(tableRowSchema),
})

export type TableData = z.infer<typeof tableSchema>
export type TableColumn = z.infer<typeof tableColumnSchema>
export type TableRow = z.infer<typeof tableRowSchema>

const chartCardSchema = z.object({
  kind: z.literal('chart'),
  cardId: z.number(),
  name: z.string(),
  /**
   * Managerium forwards Metabase's `display` (or a deep dive's override). The UI
   * draws `bar` as bars, `map` as a map and every other type as a line or area.
   * A type Managerium doesn't list here fails the parse loudly (an error tile)
   * instead of being drawn wrongly.
   */
  chartType: z.enum(chartTypes),
  unit: z.string().nullish(),
  format: cardFormatSchema.default(defaultFormat),
  /** Headline: the sum of all series at the latest / previous category. */
  value: z.number().nullish(),
  previousValue: z.number().nullish(),
  /**
   * What `value` vs `previousValue` compare when it isn't the timeframe's own
   * label, e.g. 'YoY' for a YTD tile that leads with the year-end outlook.
   */
  comparison: z.string().nullish(),
  /**
   * The year-to-date figure against the same month a year ago, which the
   * expanded card leads with in place of `value` (the tile's outlook).
   */
  reported: reportedSchema.nullish(),
  /**
   * Only present when a market subset was selected: false means the card has
   * no market column, so its figures cover all markets.
   */
  marketFiltered: z.boolean().nullish(),
  // @TODO `goalValue` (the global target line) is sent but not drawn: the current
  // design has none. Check whether future designs bring it back; if not, remove
  // it here (and its spec) and from Managerium (`goalValue`, `show-goal`).
  /** Per-category sum of all series, aligned with `categories`. */
  totals: z.array(z.number().nullable()).default([]),
  categories: z.array(z.string()),
  series: z.array(chartSeriesSchema),
  /** The range of months the series was sliced to. */
  period: z.object({ periods: z.number(), label: z.string() }).optional(),
  display: cardDisplaySchema.optional(),
  /** The number format (and name) of the right-hand y axis of series with `axis: 'secondary'`. */
  secondary: z
    .object({ format: cardFormatSchema, label: z.string().nullish() })
    .nullish(),
  /** chartType 'table': the table to draw (categories and series may then be empty). */
  table: tableSchema.optional(),
})

/** What a card shows instead when the market filter leaves nothing to show. */
const emptyReasonSchema = z.enum(['market-not-available', 'no-rows'])
export type EmptyReason = z.infer<typeof emptyReasonSchema>

const emptyCardSchema = z.object({
  kind: z.literal('empty'),
  cardId: z.number(),
  name: z.string(),
  reason: emptyReasonSchema,
})

export const cardSchema = z.discriminatedUnion('kind', [
  kpiCardSchema,
  chartCardSchema,
  emptyCardSchema,
])

export type Card = z.infer<typeof cardSchema>
export type ChartCard = z.infer<typeof chartCardSchema>
